import { useLayoutEffect, useEffect, useRef, useState, useCallback, type RefObject } from "react";

interface UseTruncateTextOptions {
  text: string;
  ellipsis?: string;
  containerRef?: RefObject<HTMLElement | null>;
  containerOffset?: number;
}

export function useTruncateText({
  text,
  ellipsis = "...",
  containerRef,
  containerOffset = 0,
}: UseTruncateTextOptions) {
  const ref = useRef<HTMLSpanElement>(null);
  const [truncatedText, setTruncatedText] = useState(text);
  const [isTruncated, setIsTruncated] = useState(false);
  const lastWidthRef = useRef<number>(0);
  const rafIdRef = useRef<number | null>(null);

  const computeTruncation = useCallback(() => {
    const element = ref.current;
    if (!element) return;

    let containerWidth: number;
    if (containerRef?.current) {
      // containerRef の内部幅（padding/border 除く）を使う
      const style = window.getComputedStyle(containerRef.current);
      const paddingLeft = parseFloat(style.paddingLeft) || 0;
      const paddingRight = parseFloat(style.paddingRight) || 0;
      containerWidth =
        containerRef.current.clientWidth - paddingLeft - paddingRight - containerOffset;
    } else {
      // span は width:100% で親の content area を埋めるため、subpixel 込みで
      // getBoundingClientRect の値を使う (offsetWidth は整数で丸め誤差が出る)
      containerWidth = element.getBoundingClientRect().width;
    }

    if (containerWidth === lastWidthRef.current) {
      return;
    }
    lastWidthRef.current = containerWidth;

    if (containerWidth === 0) {
      return;
    }

    const tempElement = document.createElement("span");
    tempElement.style.visibility = "hidden";
    tempElement.style.position = "absolute";
    tempElement.style.whiteSpace = "nowrap";

    const styles = window.getComputedStyle(element);
    tempElement.style.font = styles.font;
    tempElement.style.fontSize = styles.fontSize;
    tempElement.style.fontFamily = styles.fontFamily;
    tempElement.style.fontWeight = styles.fontWeight;
    tempElement.style.fontStyle = styles.fontStyle;
    tempElement.style.letterSpacing = styles.letterSpacing;

    document.body.appendChild(tempElement);

    tempElement.textContent = text;
    const fullTextWidth = tempElement.getBoundingClientRect().width;

    // subpixel 丸め誤差吸収のため 1px トレランスを許容 (ぴったり収まる時の誤検知を防ぐ)
    if (fullTextWidth <= containerWidth + 1) {
      setTruncatedText((prev) => (prev !== text ? text : prev));
      setIsTruncated((prev) => (prev !== false ? false : prev));
    } else {
      let left = 0;
      let right = text.length;
      let result = "";

      while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        const testText = text.slice(0, mid) + ellipsis;
        tempElement.textContent = testText;
        const testWidth = tempElement.getBoundingClientRect().width;

        if (testWidth <= containerWidth) {
          result = testText;
          left = mid + 1;
        } else {
          right = mid - 1;
        }
      }

      const newText = result || ellipsis;
      setTruncatedText((prev) => (prev !== newText ? newText : prev));
      setIsTruncated((prev) => (prev !== true ? true : prev));
    }

    document.body.removeChild(tempElement);
  }, [text, ellipsis, containerRef, containerOffset]);

  // useLayoutEffect+setState はペイント前にDOM計測→state更新するための正当なパターン
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    lastWidthRef.current = 0;
    computeTruncation();
  }, [computeTruncation]);

  // フォント読み込み完了後に再計測 (フォント未ロード時の初回計測ではメトリクスが
  // 異なり、誤って truncate されるケースがあるため)
  useEffect(() => {
    if (typeof document === "undefined" || !("fonts" in document)) return;
    const fonts = (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts;
    if (!fonts?.ready) return;
    let cancelled = false;
    fonts.ready.then(() => {
      if (cancelled) return;
      lastWidthRef.current = 0;
      computeTruncation();
    });
    return () => {
      cancelled = true;
    };
  }, [computeTruncation]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver(() => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      lastWidthRef.current = 0;
      rafIdRef.current = requestAnimationFrame(computeTruncation);
    });

    resizeObserver.observe(element);
    // 親要素 (flex container 等) もリサイズ監視: span 自身の offsetWidth が
    // 親のリサイズに追従しないケース (auto width 親のサイズが変わっても
    // span 内容が同じだと span の rect が変化しない) で再計算を発火
    if (element.parentElement) {
      resizeObserver.observe(element.parentElement);
    }
    if (containerRef?.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [computeTruncation, containerRef]);

  return { ref, truncatedText, isTruncated };
}
